import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { TransitionProvider, usePageTransition } from './context/TransitionContext.jsx';
import { getPage } from './config/navigation.js';
import { features } from './config/site.js';
import Header from './components/Header.jsx';
import Footer from './components/Footer.jsx';
import Home from './pages/Home.jsx';
import Listen from './pages/Listen.jsx';
import Projects from './pages/Projects.jsx';
import ProjectPage from './pages/ProjectPage.jsx';
import CardPage from './pages/CardPage.jsx';
import About from './pages/About.jsx';
import Contact from './pages/Contact.jsx';
import Blog from './pages/Blog.jsx';
import BlogPost from './pages/BlogPost.jsx';
import './assets/css/src/transitions.css';

function pageClass(pathname) {
  if (pathname === '/') return 'page-home';
  const parts = pathname.replace(/^\//, '').split('/');
  if (parts.length > 1) {
    // e.g. /projects/echoes-of-silence → "page-projects echoes-of-silence"
    return 'page-' + parts[0] + ' ' + parts.slice(1).join('-');
  }
  return 'page-' + parts[0];
}

function AppLayout() {
  const { phase } = usePageTransition();
  const location = useLocation();

  // Mirror transition phase onto body so body-bg CSS animations can hook in
  useEffect(() => {
    document.body.classList.remove('is-exiting', 'is-entering');
    if (phase !== 'idle') document.body.classList.add(`is-${phase}`);
  }, [phase]);

  // Apply per-page theme class to body
  useEffect(() => {
    const theme = getPage(location.pathname).theme;
    const other = theme === 'black' ? 'light' : 'black';
    document.body.classList.remove(`theme-${other}`);
    document.body.classList.add(`theme-${theme}`);
  }, [location.pathname]);

  // Apply has-blog class when blog feature is enabled
  useEffect(() => {
    if (features.blog) {
      document.body.classList.add('has-blog');
    } else {
      document.body.classList.remove('has-blog');
    }
  }, []);

  return (
    <div
      id="wrapper"
      className={[
        phase !== 'idle' ? `is-${phase}` : '',
        pageClass(location.pathname),
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <Header />
      <main>
        <article id="page" className="main-content">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/listen" element={<Listen />} />
            <Route path="/projects" element={<Projects />} />
            <Route path="/projects/:slug" element={<ProjectPage />} />
            <Route path="/projects/:slug/:cardSlug" element={<CardPage />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            {features.blog && <>
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
            </>}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </article>
      </main>
      <Footer />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <TransitionProvider>
        <AppLayout />
      </TransitionProvider>
    </BrowserRouter>
  );
}
